import { useInView } from "../../hooks/useInView";
import { siteConfig } from "../../config/site";

const STEPS = [
  {
    number: "01",
    step: "STEP 1",
    title: "Check Eligibility",
    description: "See which loan options you may qualify for.",
    accent: false,
  },
  {
    number: "02",
    step: "STEP 2",
    title: "Apply Online",
    description: "Fill out your application with the required details.",
    accent: false,
  },
  {
    number: "03",
    step: "STEP 3",
    title: "Get a Decision",
    description:
      "Your application is reviewed based on the applicable eligibility criteria.",
    accent: false,
  },
  {
    number: "04",
    step: "STEP 4",
    title: "Get Your Funds",
    description:
      "Once approved and the loan agreement is completed, funds are disbursed according to the applicable terms.",
    accent: true,
  },
];

export function HowItWorks() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section className="w-full bg-[#0d0b14] px-4 py-16 sm:py-24">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-6">
        {/* Header */}
        <div
          ref={ref}
          className={`flex max-w-[800px] flex-col items-center gap-6 text-center ${inView ? "in-view" : ""}`}
        >
          <span className="reveal-scroll delay-1 rounded-full border border-solid border-white/20 px-[14px] py-[6px] text-xs font-semibold uppercase tracking-[0.6px] text-white">
            How It Works
          </span>
          <h2 className="reveal-scroll delay-2 text-3xl font-extrabold leading-tight tracking-[-1px] text-white sm:text-5xl lg:text-[48px]">
            A simple loan process in{" "}
            <span className="text-[#a78bfa]">4 easy</span> steps.
          </h2>
          <p className="reveal-scroll delay-3 max-w-[640px] text-base leading-7 text-[#a8a29e]">
            From checking your eligibility to getting your funds, our digital
            process keeps things simple and straightforward.
          </p>
        </div>

        {/* Steps grid */}
        <div className="mt-4 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, idx) => (
            <div
              key={step.number}
              className={`reveal-scroll flex min-h-[439px] flex-col rounded-[24px] p-7 ${
                step.accent ? "bg-[#0e605c]" : "bg-[#f5f2fc]"
              }`}
              style={{
                animationDelay: `${0.4 + idx * 0.1}s`,
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`flex h-6 w-8 items-center justify-center rounded-full text-xs font-bold ${
                    step.accent
                      ? "bg-white/20 text-white"
                      : "bg-[#e2ddec] text-[#a8a29e]"
                  }`}
                >
                  {step.number}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-[0.5px] ${
                    step.accent ? "text-white/70" : "text-[#a8a29e]"
                  }`}
                >
                  {step.step}
                </span>
              </div>

              <h3
                className={`pt-4 text-xl font-bold leading-7 ${
                  step.accent ? "text-white" : "text-[#1c1917]"
                }`}
              >
                {step.title}
              </h3>
              <p
                className={`pt-2 text-sm leading-5 ${
                  step.accent ? "text-white/80" : "text-[#57534e]"
                }`}
              >
                {step.description}
              </p>

              <div className="mt-auto pt-6">
                {step.number === "01" ? (
                  <div className="rounded-2xl bg-white p-[17px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#10b981] text-[10px] text-white">
                        ✓
                      </span>
                      <span className="h-2 w-24 rounded-full bg-[#e5e7eb]" />
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#10b981] text-[10px] text-white">
                        ✓
                      </span>
                      <span className="h-2 w-16 rounded-full bg-[#e5e7eb]" />
                    </div>
                    <div className="mt-3 rounded-full bg-[#3e025c] py-2 text-center text-xs font-semibold text-white">
                      Qualified to $25,000
                    </div>
                  </div>
                ) : null}

                {step.number === "02" ? (
                  <div className="rounded-2xl bg-white p-[17px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#a8a29e]">
                        Application
                      </span>
                      <span className="text-[11px] font-semibold text-[#176766]">
                        80% Done
                      </span>
                    </div>
                    <div className="mt-2 h-[6px] w-full rounded-full bg-[#f5f5f4]">
                      <div className="h-full w-[80%] rounded-full bg-[#176766]" />
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-[#57534e]">Loan Amount</span>
                      <span className="font-bold text-[#1c1917]">
                        $25,000
                      </span>
                    </div>
                  </div>
                ) : null}

                {step.number === "03" ? (
                  <div className="rounded-2xl bg-white p-[17px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#10b981]" />
                      <span className="text-xs font-semibold text-[#1c1917]">
                        Instant In-Principle
                      </span>
                    </div>
                    <p className="mt-2 text-xs italic leading-4 text-[#57534e]">
                      &quot;Congratulations! Decision approved under 30
                      seconds.&quot;
                    </p>
                  </div>
                ) : null}

                {step.number === "04" ? (
                  <div className="flex items-center justify-between rounded-2xl border border-solid border-white/10 bg-white/10 p-[17px]">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-xs text-white">
                        ⊗
                      </span>
                      <div>
                        <p className="text-xs font-bold text-white">
                          Direct Transfer
                        </p>
                        <p className="text-[10px] text-white/70">
                          Acct ending in *4890
                        </p>
                      </div>
                    </div>
                    <span className="rounded-full bg-[#34d399]/20 px-2 py-1 text-[10px] font-bold text-[#34d399]">
                      ACTIVE
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <a
          href={siteConfig.applyLink || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center justify-center rounded-full bg-[#3e025c] px-8 py-[14px] text-sm font-semibold text-white shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1)] transition-opacity hover:opacity-90"
        >
          Apply For Loan
        </a>
      </div>
    </section>
  );
}
